import { DateTime } from 'luxon'

import { compareStoreCatalogItemsByCoin } from '@/shared/lib/array/sort-store-catalog-by-coin'
import { checkBlockCondition, type GetProgress } from '@/shared/lib/blocker/checkBlockCondition'
import type { RewardWithUnderscores } from '@/types/reward'

import {
  isRedeemableNow,
  type StoreNudgeCatalogItem,
  type StoreNudgeRewardRequest,
  type StoreStatus
} from './isRedeemableNow'
import { STORE_NUDGE_THUMBS_CAP } from '../model/constants'


import type {
  StoreNudgeDerivation,
  StoreNudgeSeenState,
  StoreNudgeUnlocked
} from '../model/types'

export type FinishedSets = {
  journeys: Set<number>
  missions: Set<number>
  levels: Set<number>
  partners: Set<number>
}

/** Mesma tabela de `useBlocker.defaultGetProgress`, sobre conjuntos. */
export const buildGetProgress = (finished: FinishedSets): GetProgress => (
  resource,
  condition
) => {
  switch (resource.type) {
    case 'journey':
      return finished.journeys.has(resource.id)
    case 'mission':
      return finished.missions.has(resource.id)
    case 'level':
      return condition === 'blockRedo'
        ? !finished.levels.has(resource.id)
        : finished.levels.has(resource.id)
    case 'partner':
      return finished.partners.has(resource.id)
    default:
      return true
  }
}

const hasUnlockCondition = (reward: RewardWithUnderscores) =>
  reward._blockCondition?.type === 'and' || reward._blockCondition?.type === 'or'
    ? reward._blockCondition.conditions.length > 0
    : Boolean(reward._blockCondition)

const isUnlocked = (reward: RewardWithUnderscores, getProgress: GetProgress) =>
  checkBlockCondition(reward._blockCondition, getProgress).unlocked

/**
 * Primeira execução: marca como anunciados os rewards já destravados para não
 * disparar um backlog de popups no deploy. O anúncio de moedas não entra aqui.
 */
export const seedSeenStateFromCurrent = (
  rewards: RewardWithUnderscores[],
  getProgress: GetProgress
): StoreNudgeSeenState => ({
  affordAnnounced: false,
  announcedRewardIds: rewards
    .filter(r => hasUnlockCondition(r) && isUnlocked(r, getProgress))
    .map(r => r.id),
  lastShownAt: null
})

export type DeriveInput = {
  coins: number
  finished: FinishedSets
  rewards: RewardWithUnderscores[]
  rewardItems: StoreNudgeCatalogItem[]
  rewardRequests: StoreNudgeRewardRequest[]
  storeStatus: StoreStatus
  seen: StoreNudgeSeenState
  now?: DateTime
}

export const hasCandidates = (derivation: StoreNudgeDerivation) =>
  derivation.affordable !== null || derivation.unlocked.length > 0

/**
 * Decide o que merece anúncio a partir do que o cliente já tem. Puro: roda em
 * `node` e é recalculado só quando progresso, catálogo ou pedidos mudam.
 */
export const deriveStoreNudgeCandidates = ({
  coins,
  finished,
  rewards,
  rewardItems,
  rewardRequests,
  storeStatus,
  seen,
  now
}: DeriveInput): StoreNudgeDerivation => {
  const getProgress = buildGetProgress(finished)
  const rewardById = new Map(rewards.map(r => [r.id, r]))
  const requestsByReward = new Map<number, StoreNudgeRewardRequest[]>()
  for (const request of rewardRequests) {
    const list = requestsByReward.get(request.reward_id) ?? []
    list.push(request)
    requestsByReward.set(request.reward_id, list)
  }

  const redeemable = rewardItems
    .filter(item =>
      isRedeemableNow(item, {
        reward: rewardById.get(item.reward_id),
        storeStatus,
        requestsOfReward: requestsByReward.get(item.reward_id) ?? [],
        getProgress,
        now
      })
    )
    .sort(compareStoreCatalogItemsByCoin)

  const visibleItems = rewardItems.filter(item => {
    const reward = rewardById.get(item.reward_id)
    return Boolean(reward?.active) && item.active && !item.sold_out
  })

  const cheapest = redeemable[0]
  const affordable =
    cheapest && !seen.affordAnnounced && coins >= (cheapest.coin ?? Infinity)
      ? { item: cheapest }
      : null

  const announced = new Set(seen.announcedRewardIds)
  const unlocked: StoreNudgeUnlocked[] = []
  for (const reward of rewards) {
    if (!hasUnlockCondition(reward)) continue
    if (announced.has(reward.id)) continue
    if (!isUnlocked(reward, getProgress)) continue
    const highlightItem = redeemable.find(item => item.reward_id === reward.id)
    if (!highlightItem) continue
    unlocked.push({
      reward,
      highlightItem,
      affordable: coins >= (highlightItem.coin ?? Infinity)
    })
  }

  const highlightIds = new Set<number>([
    ...(affordable ? [affordable.item.id] : []),
    ...unlocked.map(u => u.highlightItem.id)
  ])
  const others = visibleItems.filter(item => !highlightIds.has(item.id))
  const thumbs = others.slice(0, STORE_NUDGE_THUMBS_CAP)

  return {
    affordable,
    unlocked,
    thumbs,
    thumbsOverflow: others.length - thumbs.length,
    othersCount: others.length
  }
}
